// src/modulos/Parametros/Tipos/TypePositionPage.js
import React, { useState, useMemo, useCallback } from 'react';
import useSubCollection from '../../../hooks/useSubCollection';
import usePagePermissions from '../../../hooks/usePagePermissions';

// Import global components
import {
    PageHeader,
    SearchBar,
    FormModal,
    ConfirmModal,
    TableSkeleton,
    EmptyState,
    Pagination,
} from '../../../components/common';

// Import utilities
import { getTimestamps } from '../../../utils/timestamps';

// Page-specific styles
import styles from '../Parameters.module.css';

// Constantes
const POSITION_COLLECTION_PATH = {
    parentCollection: 'parameters',
    parentId: 'types',
    subCollectionName: 'funcao'
};
const ITEMS_PER_PAGE = 10;

const TypePositionPage = () => {
    const can = usePagePermissions('tipo_de_funcao');

    // Estados Locais
    const [editingPosition, setEditingPosition] = useState(null);
    const [inputValue, setInputValue] = useState('');
    const [formLoading, setFormLoading] = useState(false);
    const [formError, setFormError] = useState('');
    const [formSuccess, setFormSuccess] = useState('');
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [searchTerm, setSearchTerm] = useState('');
    const [deleteConfirm, setDeleteConfirm] = useState({ isOpen: false, id: null, name: '' });
    const [deleteLoading, setDeleteLoading] = useState(false);

    // Hook de Dados
    const {
        data: positions,
        loading: listLoading,
        error: listError,
        addDocument,
        updateDocument,
        deleteDocument,
        refreshData
    } = useSubCollection(
        POSITION_COLLECTION_PATH.parentCollection,
        POSITION_COLLECTION_PATH.parentId,
        POSITION_COLLECTION_PATH.subCollectionName,
        'name'
    );

    // ============================================================
    // 1. DEFINIR filteredPositions ANTES dos callbacks
    // ============================================================
    const filteredPositions = useMemo(() => {
        if (!positions) return [];
        if (!searchTerm) return positions;

        const lowerCaseSearchTerm = searchTerm.toLowerCase();
        return positions.filter(item =>
            item.name.toLowerCase().includes(lowerCaseSearchTerm)
        );
    }, [positions, searchTerm]);

    const totalFilteredItems = filteredPositions.length;
    const totalPagesAfterFilter = Math.ceil(totalFilteredItems / ITEMS_PER_PAGE);

    const paginatedPositions = useMemo(() => {
        const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
        return filteredPositions.slice(startIndex, startIndex + ITEMS_PER_PAGE);
    }, [filteredPositions, currentPage]);

    // Reset do formulário
    const resetForm = useCallback(() => {
        setEditingPosition(null);
        setInputValue('');
        setFormError('');
        setFormSuccess('');
    }, []);

    const handleCloseModal = useCallback(() => {
        setIsModalOpen(false);
        resetForm();
    }, [resetForm]);

    const handleNewClick = useCallback(() => {
        resetForm();
        setIsModalOpen(true);
    }, [resetForm]);

    const handleEditClick = useCallback((item) => {
        setEditingPosition(item);
        setInputValue(item.name);
        setFormError('');
        setFormSuccess('');
        setIsModalOpen(true);
    }, []);

    const handleDeleteClick = useCallback((id, name) => {
        setDeleteConfirm({ isOpen: true, id, name });
    }, []);

    // Confirmar Delete
    const handleConfirmDelete = useCallback(async () => {
        const { id, name } = deleteConfirm;
        setDeleteLoading(true);

        try {
            const result = await deleteDocument(id);

            if (result.success) {
                setFormSuccess(`Função "${name}" deletada com sucesso!`);
                setDeleteConfirm({ isOpen: false, id: null, name: '' });

                const newTotalItems = totalFilteredItems - 1;
                const maxPage = Math.ceil(newTotalItems / ITEMS_PER_PAGE);
                if (currentPage > maxPage && maxPage > 0) {
                    setCurrentPage(maxPage);
                } else if (newTotalItems === 0) {
                    setCurrentPage(1);
                }

                setTimeout(() => setFormSuccess(''), 3000);
            } else {
                setFormError("Erro ao deletar Função: " + result.error);
                setDeleteConfirm({ isOpen: false, id: null, name: '' });
                setTimeout(() => setFormError(''), 5000);
            }
        } catch (error) {
            setFormError('Erro ao deletar função. Tente novamente.');
            setDeleteConfirm({ isOpen: false, id: null, name: '' });
        } finally {
            setDeleteLoading(false);
        }
    }, [deleteConfirm, deleteDocument, totalFilteredItems, currentPage]);

    // Submissão do formulário
    const handleFormSubmit = useCallback(async (e) => {
        e.preventDefault();
        setFormError('');
        setFormSuccess('');

        if (!inputValue.trim()) {
            setFormError("O nome da Função é obrigatório.");
            return;
        }

        const userEmail = localStorage.getItem('userEmail') || 'system';
        const timestamps = getTimestamps(editingPosition, userEmail);
        const data = {
            name: inputValue.trim(),
            ...timestamps,
        };

        setFormLoading(true);

        try {
            let result;
            if (editingPosition) {
                result = await updateDocument(editingPosition.id, data);
            } else {
                result = await addDocument(data);
            }

            if (result.success) {
                setFormSuccess(editingPosition ? 'Função editada com sucesso!' : 'Função adicionada com sucesso!');
                setTimeout(() => {
                    handleCloseModal();
                }, 1500);
            } else {
                setFormError(`Falha ao ${editingPosition ? 'editar' : 'adicionar'}: ${result.error}`);
            }
        } catch (error) {
            setFormError('Erro ao salvar no banco de dados. Tente novamente.');
        } finally {
            setFormLoading(false);
        }
    }, [inputValue, editingPosition, addDocument, updateDocument, handleCloseModal]);

    // Paginação
    const handlePageChange = useCallback((newPage) => {
        if (newPage > 0 && newPage <= totalPagesAfterFilter) {
            setCurrentPage(newPage);
        }
    }, [totalPagesAfterFilter]);

    const handleSearch = useCallback((term) => {
        setSearchTerm(term);
        setCurrentPage(1);
    }, []);

    return (
        <div className={styles.pageContainer}>
            {/* Form Modal */}
            <FormModal
                isOpen={isModalOpen}
                onClose={handleCloseModal}
                onSubmit={handleFormSubmit}
                title={editingPosition ? `Editar: ${editingPosition.name}` : 'Adicionar Nova Função'}
                subtitle={editingPosition ? `Editando: ${editingPosition.name}` : undefined}
                isLoading={formLoading}
                error={formError}
                success={formSuccess}
                submitText={editingPosition ? '💾 Salvar Edição' : '✨ Adicionar Função'}
            >
                <div className={styles.inputGroup}>
                    <label htmlFor="name">
                        Nome da Função <span className={styles.required}>*</span>
                    </label>
                    <input
                        id="name"
                        type="text"
                        value={inputValue}
                        onChange={(e) => setInputValue(e.target.value)}
                        className={styles.input}
                        placeholder="Ex: Vendas, Produção, RH"
                        required
                        disabled={formLoading}
                        autoFocus
                    />
                    <small className={styles.fieldHint}>
                        Nome que identifica a função ou setor
                    </small>
                </div>
            </FormModal>

            {/* Delete Confirmation Modal */}
            <ConfirmModal
                isOpen={deleteConfirm.isOpen}
                onClose={() => setDeleteConfirm({ isOpen: false, id: null, name: '' })}
                onConfirm={handleConfirmDelete}
                title="⚠️ Confirmar Exclusão"
                message={`Tem certeza que deseja deletar a Função "${deleteConfirm.name}"?\n\nEsta ação não poderá ser desfeita.`}
                isLoading={deleteLoading}
                danger={true}
            />

            {/* Page Header */}
            <PageHeader
                title="Tipos de Função"
                actionText="Adicionar Função"
                onAction={handleNewClick}
                disabled={formLoading}
                showAction={can.create}
            />

            {/* Search Bar */}
            <div className={styles.searchBar}>
                <SearchBar
                    placeholder="Buscar tipo de função..."
                    onSearch={handleSearch}
                    disabled={listLoading || formLoading}
                />
            </div>

            {/* Error State */}
            {listError && (
                <div className={styles.errorContainer}>
                    <div className={styles.errorIcon}>⚠️</div>
                    <p className={styles.errorText}>Erro ao carregar lista: {listError}</p>
                    <button onClick={refreshData} className={styles.retryButton}>
                        Tentar Novamente
                    </button>
                </div>
            )}

            {/* Table or Loading State */}
            {!listError && (
                <>
                    {listLoading ? (
                        <TableSkeleton columns={2} rows={ITEMS_PER_PAGE} />
                    ) : paginatedPositions.length === 0 ? (
                        <EmptyState
                            icon={searchTerm ? '🔍' : '👔'}
                            title={
                                searchTerm
                                    ? 'Nenhum resultado encontrado'
                                    : 'Nenhuma função cadastrada'
                            }
                            message={
                                searchTerm
                                    ? `Não encontramos resultados para "${searchTerm}"`
                                    : "Clique em '+ Adicionar Função' para começar"
                            }
                        />
                    ) : (
                        <>
                            <div className={styles.tableContainer}>
                                <table className={styles.table}>
                                    <thead>
                                        <tr>
                                            <th>Nome da Função</th>
                                            <th style={{ width: '150px' }}>Ações</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {paginatedPositions.map((item) => (
                                            <tr key={item.id} className={styles.tableRow}>
                                                <td className={styles.conditionName}>
                                                    <strong>{item.name}</strong>
                                                    {item.updatedAt && (
                                                        <small className={styles.rowMetadata}>
                                                            Atualizado: {new Date(item.updatedAt).toLocaleDateString('pt-BR')}
                                                        </small>
                                                    )}
                                                </td>
                                                <td className={styles.actionsCell}>
                                                    {can.edit && (
                                                        <button
                                                            onClick={() => handleEditClick(item)}
                                                            className={styles.actionButton}
                                                            disabled={formLoading}
                                                        >
                                                            Editar
                                                        </button>
                                                    )}
                                                    {can.delete && (
                                                        <button
                                                            onClick={() => handleDeleteClick(item.id, item.name)}
                                                            className={`${styles.actionButton} ${styles.deleteButton}`}
                                                            disabled={formLoading}
                                                        >
                                                            Deletar
                                                        </button>
                                                    )}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>

                            <Pagination
                                currentPage={currentPage}
                                totalPages={totalPagesAfterFilter}
                                totalItems={totalFilteredItems}
                                onPageChange={handlePageChange}
                                disabled={formLoading}
                                itemsLabel="funções"
                            />
                        </>
                    )}
                </>
            )}
        </div>
    );
};

export default TypePositionPage;